import React from 'react';
import { createRoot } from 'react-dom/client';
import algoliasearch from 'algoliasearch/lite';
import { history } from 'instantsearch.js/es/lib/routers';
import {
    useInstantSearch,
    InstantSearch,
    Configure,
    ClearRefinements,
    InfiniteHits,
    SearchBox,
    Stats
  } from 'react-instantsearch';

import { CardProductEntry } from "./product-card";
import { MarketFilter } from "./market-filter";
import { FilterPanel } from "./filter-panel";

const ALGOLIA_APPLICATION_ID = import.meta.env.VITE_ALGOLIA_APPLICATION_ID;
const ALGOLIA_SEARCH_API_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY;
const searchClient = algoliasearch(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);

export default () => {
    const root = createRoot(document.getElementById('app'));
    root.render(<Index/>);
}

// Display the No Results message when no hits have been returned.
function NoResultsBoundary({ children, fallback }) {
    const { results } = useInstantSearch();
    if (!results.__isArtificial && results.nbHits === 0) {
        return (
            <>
            {fallback}
            <div hidden>{children}</div>
            </>
        );
    }
    return children;
}
  
function NoResults() {
    const { indexUiState } = useInstantSearch();
    return (
      <div className="text-center my-12 mx-auto text-lg text-brand-blue-900">
        <p>No results for <q>{indexUiState.query}</q>.</p>
      </div>
    );
}


const getPageMeta = async (title) => {

    const ApiUrl = import.meta.env.VITE_GRAPHQL_URL;

    let returnData  = await fetch(ApiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            query: `query CategoryMeta {
                entries(title: "${title}", section: "resourceCat") {
                title
                url
                seomatic(asArray: true) {
                    metaTitleContainer
                    metaTagContainer
                }
            }
        }`
        })
    });
    return await returnData.json();

}


const getPageMetaNew = async () => {

    const ApiUrl = import.meta.env.VITE_GRAPHQL_URL;

    let returnData  = await fetch(ApiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            query: `query SeoMeta {
                entries(section: "productsSingle") {
                    title
                    url
                    ... on productsSingle_productsSingle_Entry {
                        metaDescs {
                            urlParams
                            metaDescription
                        }
                    }
                }
            }`
        })
    });
    return await returnData.json();

}


function Index() {

    const searchParams = new URLSearchParams(document.location.search);
    let filterDepartment  = searchParams.get("department") ?? '';
    let filterStyle       = searchParams.get("style") ?? '';
    let filterCollection  = searchParams.get("collection") ?? '';
    let filterTemperature = searchParams.get("temperature") ?? '';
    let filterMarket      = searchParams.get("market") ?? '';
    let filterFunction    = searchParams.get("productFunction") ?? '';
    let searchQuery       = searchParams.get("show") ?? '';

    const routing = {
        router: history({


            windowTitle(routeState) {

                const show = routeState.show;
                const queryTitle = show ? `Results for "${show}" | Structural Concepts` : 'Products | Structural Concepts';

                let pageMeta = {
                    title: queryTitle,
                    description: null
                };

                const department = routeState.department;
                const style = routeState.style;
                const temperature = routeState.temperature;
                const collection = routeState.collection;
                const market = routeState.market;
                const productFunction = routeState.function;

                const getActivity = async (type) => {

                    let entryType = null;
                    if (type === 'department') {
                        entryType = department[0];
                    } else if (type === 'style') {
                        entryType = style[0];
                    } else if (type === 'temperature') {
                        entryType = temperature[0];
                    } else if (type === 'collection') {
                        entryType = collection[0];
                    } else if (type === 'market') {
                        entryType = market[0];
                    } else if (type === 'function') {
                        entryType = productFunction[0];
                    } else {
                        return null;
                    }

                    let jsonData = await getPageMeta(entryType);
                    let pageTitle = JSON.parse(jsonData.data.entries[0].seomatic.metaTitleContainer).title.title;
                    //let pageDescription = JSON.parse(jsonData.data.entries[0].seomatic.metaTagContainer).description.content;

                    let pageDescription = "Explore Structural Concepts' full product range for retail merchandisers designed to boost sales.";
                    let jsonDataNew = await getPageMetaNew();
                    let metaDescs = jsonDataNew.data.entries[0].metaDescs;

                    let currentQueryString = `${type}=${encodeURIComponent(entryType)}`;
                    if (metaDescs) {
                        metaDescs.forEach(row => {
                            if (row.urlParams == currentQueryString && row.metaDescription != "") {
                                pageDescription = row.metaDescription;
                            }
                        });
                    }

                    pageMeta.title = pageTitle;
                    pageMeta.description = pageDescription;

                    if (pageMeta.description !== null) {
                        document.title = pageMeta.title;
                        document.querySelector('meta[name="description"]').setAttribute("content", pageMeta.description);
                        document.querySelector("meta[name='twitter:description'" ).setAttribute("content", pageMeta.description);
                        document.querySelector("meta[property='og:description'" ).setAttribute("content", pageMeta.description);
                    }

                    return pageMeta;

                }

                if (department && Object.keys(department).length === 1) {
                    getActivity('department');
                } else if (style && Object.keys(style).length === 1) {
                    getActivity('style');
                } else if (temperature && Object.keys(temperature).length === 1) {
                    getActivity('temperature');
                } else if (collection && Object.keys(collection).length === 1) {
                    getActivity('collection');
                } else if (market && Object.keys(market).length === 1) {
                    getActivity('market');
                } else if (productFunction && Object.keys(productFunction).length === 1) {
                    getActivity('function');
                } else {
                    return pageMeta.title;
                }


            },

            createURL({ qsModule, routeState, location }) {

                const urlParts = location.href.match(/^(.*?)\/products/);
                const baseUrl = `${urlParts ? urlParts[1] : ''}/`;

                const queryParameters = {};

                if (routeState.show) {
                    queryParameters.show = encodeURIComponent(routeState.show);
                }

                if (routeState.page !== 1) {
                    queryParameters.page = routeState.page;
                }

                if (routeState.department) {
                    queryParameters.department = routeState.department.map(encodeURIComponent);
                }

                if (routeState.style) {
                    queryParameters.style = routeState.style.map(encodeURIComponent);
                }

                if (routeState.temperature) {
                    queryParameters.temperature = routeState.temperature.map(encodeURIComponent);
                }

                if (routeState.collection) {
                    queryParameters.collection = routeState.collection.map(encodeURIComponent);
                }

                if (routeState.market) {
                    queryParameters.market = routeState.market.map(encodeURIComponent);
                }

                if (routeState.function) {
                    queryParameters.productFunction = routeState.function.map(encodeURIComponent);
                }

                const queryString = qsModule.stringify(queryParameters, {
                    addQueryPrefix: true,
                    arrayFormat: 'repeat',
                });

                return `${baseUrl}products/${queryString}`;
            },


            parseURL({ qsModule, location }) {

                // const pathnameMatches = location.pathname.match(/products\/(.*?)\/?$/);

                const { show = '', department = [], style = [], temperature = [], collection = [], market = [], productFunction = [], page } = qsModule.parse(location.search.slice(1));


                // `qs` does not return an array when there's a single value.
                const allDepts = Array.isArray(department) ? department : [department].filter(Boolean);
                const allStyles = Array.isArray(style) ? style : [style].filter(Boolean);
                const allTemps = Array.isArray(temperature) ? temperature : [temperature].filter(Boolean);
                const allCollections = Array.isArray(collection) ? collection : [collection].filter(Boolean);
                const allMarkets = Array.isArray(market) ? market : [market].filter(Boolean);
                const allFunctions = Array.isArray(productFunction) ? productFunction : [productFunction].filter(Boolean);

                return {
                    show: decodeURIComponent(show),
                    department: allDepts.map(decodeURIComponent),
                    style: allStyles.map(decodeURIComponent),
                    temperature: allTemps.map(decodeURIComponent),
                    collection: allCollections.map(decodeURIComponent),
                    market: allMarkets.map(decodeURIComponent),
                    function: allFunctions.map(decodeURIComponent),
                    page,
                };

            },
            cleanUrlOnDispose: false,

        }),

        stateMapping: {

            stateToRoute(uiState) {
                const indexUiState = uiState['products'] || {};
                return {
                    show: indexUiState.query,
                    department: indexUiState.refinementList?.departments,
                    style: indexUiState.refinementList?.styles,
                    temperature: indexUiState.refinementList?.temperatures,
                    collection: indexUiState.refinementList?.collections,
                    market: indexUiState.refinementList?.markets,
                    function: indexUiState.refinementList?.function,
                    page: indexUiState.page,
                };
            },

            routeToState(routeState) {
                return {
                    ['products']: {
                        query: routeState.show,
                        refinementList: {
                            departments: routeState.department,
                            styles: routeState.style,
                            temperatures: routeState.temperature,
                            collections: routeState.collection,
                            markets: routeState.market,
                            function: routeState.function,
                        },
                        page: routeState.page,
                    },
                };
            },

        },
    };

    const futureFlags = {preserveSharedStateOnUnmount: true};

    return (
        <InstantSearch
                    searchClient={searchClient}
                    indexName="products"
                    insights={true}
                    future={{
                        preserveSharedStateOnUnmount: true
                    }}
                    routing={routing}
        >
            <Configure hitsPerPage={12} />
            <div className="bg-brand-blue-50 py-8 px-4 lg:p-12">
                <div className="xl:container mx-auto">
                    <p className="text-sm mb-4">Filter by Market</p>
                    <MarketFilter attribute="markets" sortBy={['name:asc']} operator="or"/>
                </div>
            </div>
            <div className="container mx-auto">
                <div className="lg:flex gap-12 py-12 lg:py-16 xl:py-24 2xl:py-28">
                    <div className="basis-1/4 lg:sticky self-start top-8">
                            <SearchBox placeholder="Search products"
                                classNames={{
                                    root: 'w-full',
                                    form: 'relative',
                                    input: 'w-full border border-brand-neutral-400 outline-brand-blue-100 rounded-md p-4 pl-12',
                                    submit: 'absolute left-4 top-4',
                                    reset: 'absolute right-4 top-4'
                                }}
                                submitIconComponent={() => (
                                    <svg className="w-6 h-auto fill-current text-slate-400" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path></svg>
                                )}
                                resetIconComponent={({ classNames }) => (
                                    <svg className="w-6 h-auto fill-current text-slate-600" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
                                )}
                            />
                            <div className="flex items-center justify-between mt-6 py-6 border-b border-brand-50">
                                <h3 className="font-medium text-xl">Filters</h3>
                                <div className="flex items-center gap-2">
                                        <svg className="text-brand-neutral-600 w-5 h-auto" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M224,128a96,96,0,0,1-94.71,96H128A95.38,95.38,0,0,1,62.1,197.8a8,8,0,0,1,11-11.63A80,80,0,1,0,71.43,71.39a3.07,3.07,0,0,1-.26.25L44.59,96H72a8,8,0,0,1,0,16H24a8,8,0,0,1-8-8V56a8,8,0,0,1,16,0V85.8L60.25,60A96,96,0,0,1,224,128Z"></path></svg>
                                        <ClearRefinements
                                            classNames={{
                                                root: 'flex items-center',
                                                button: 'text-brand-blue-300',
                                                disabledButton: 'text-brand-neutral-600',
                                            }}
                                            translations={{
                                                resetButtonText: 'Reset Filters',
                                            }}
                                        />
                                </div>
                            </div>
                            <FilterPanel
                                heading='Department'
                                attribute='departments'
                                expand={filterDepartment !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                                limit={15}
                           />
                           <FilterPanel
                                heading='Product Style'
                                attribute='styles'
                                expand={filterStyle !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                                limit={15}
                           />
                            <FilterPanel
                                heading='Temperature'
                                attribute='temperatures'
                                expand={filterTemperature !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                                limit={15}
                           />
                            <FilterPanel
                                heading='Collection'
                                attribute='collections'
                                expand={filterCollection !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                                limit={15}
                           />
                            <FilterPanel
                                heading='Function'
                                attribute='function'
                                expand={filterFunction !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                                limit={15}
                           />
                    </div>
                    <div className="basis-3/4 relative mt-6 lg:mt-0">
                        <Stats classNames={{root: 'hidden lg:flex ml-auto text-sm text-brand-neutral-600 absolute -translate-y-8 right-0'}}/>
                        <NoResultsBoundary fallback={<NoResults />}>
                            <InfiniteHits
                                hitComponent={CardProductEntry}
                                showPrevious={false}
                                classNames={{
                                    list: 'grid sm:grid-cols-2 xl:grid-cols-3 gap-4 gap-y-8 md:gap-6 lg:gap-y-10 xl:gap-10',
                                    loadMore: 'text-brand-blue-300 hover:text-brand-blue-500 border border-brand-blue-300 hover:text-brand-blue-300 rounded-md px-8 py-4 mt-8 mx-auto flex justify-center',
                                }}
                                translations={{
                                    showMoreButtonText: 'Load More',
                                }}
                            />
                        </NoResultsBoundary>
                    </div>
                </div>
            </div>
        </InstantSearch>
    );


}